import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from "react-router";
import './App.css';
import IdlePage from "./pages/IdlePage";
import PlaySorteo from "./pages/PlaySorteo";
import PlaySorteoAnfora from "./pages/PlaySorteoAnfora";
import PlaySorteoBySala from "./pages/PlaySorteoBySala";
import CongratWinner from "./pages/CongratWinner";
import SorteoList from "./pages/SorteoList";
import IdleHasWinner from "./pages/IdleHasWinner";
import SalaSelector from "./pages/SalaSelector";
import LoginPage from "./login/LoginPage";
import axios from "axios";
import CallbackPage from "./login/CallbackPage";
import WauthProvider from "./auth/WauthProvider";
import LogoutPage from "./login/LogoutPage";
import HomePage from "./pages/HomePage";
import RequireAuth from "./auth/RequireAuth";
import AnforaView from "./pages/AnforaView";
import SorteoParticipants from "./pages/SorteoParticipants";
import PageWrapperStyled from './components/PageWrapper'
import PlayHotSeat from './pages/PlayHotSeat'
import PlayHotSeatCasino from './pages/PlayHotSeatCasino'
import PlayHotSeatBySala from './pages/PlayHotSeatBySala'
import CongratHotSeatWinner from './pages/CongratHotSeatWinner'
import SorteoMachines from './pages/SorteoMachines'
import TorneoViewRank from './pages/TorneoViewRank'
import TorneoViewFinal from './pages/TorneoViewFinal'
import TorneoViewRankPublic from "./pages/TorneoViewRankPublic"
import TorneoWinner from './pages/TorneoWinner';
import { ThemeProvider } from 'styled-components'
import { dreamsColor, generalColor, greenColor, GlobalStyles } from './theme'
import { SiteProvider } from "./context/SiteContext";
import ProgressiveView from './pages/ProgressiveView';
import ProgressiveWinnerView from './pages/ProgressiveWinnerView';
import PwaUpdatePrompt from './components/PwaUpdatePrompt';
import PerformanceMonitor from './components/PerformanceMonitor';
import { resetSalaSessionAndRedirect } from './util/salaSession';

const VirtualRafflePreview = import.meta.env.MODE === 'development'
  ? React.lazy(() => import('./pages/VirtualRafflePreview')) : null;
const ProgressivePreview = import.meta.env.MODE === 'development'
  ? React.lazy(() => import('./pages/ProgressivePreview')) : null;
const HotSeatCasinoPreview = import.meta.env.MODE === 'development'
  ? React.lazy(() => import('./pages/HotSeatCasinoPreview')) : null;

// Set the AUTH token for any request
axios.interceptors.request.use(function (config) {
  const token = localStorage.getItem('auth');
  const salaId = localStorage.getItem('salaId');
  const tenant = localStorage.getItem('tenant');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (salaId && salaId > 0) {
    config.headers['X-Sala-ID'] = salaId
  }
  if (tenant) {
    config.headers['X-Tenant'] = tenant
  }

  return config;
});

axios.interceptors.response.use(function (response) {
  return response;
}, function (error) {
  if (error.response) {
    if (401 === error.response.status) {
      // Sin sesión no hay nada que cerrar: en una vista pública (el ranking público) la llamada solo falla.
      if (localStorage.getItem('auth')) window.location.href = '/logout';
      return Promise.reject(error);
    } else if (403 === error.response.status) {
      // La sala guardada ya no es válida (sin permisos o cambiada):
      // se limpia sala/salaId y se vuelve al selector para setearla de nuevo.
      resetSalaSessionAndRedirect();
      return Promise.reject(error);
    } else {
      return Promise.reject(error);
    }
  } else {
    return Promise.reject(error);
  }
});

// Las rutas sin "exact" en react-router 5 también aceptaban subrutas: en v7 eso es el "/*" final.
const SecureApp = () => {
  return (
    <SiteProvider>
      <RequireAuth>
        <Routes>
          <Route path="/selector/*" element={<SalaSelector />} />
          <Route path="/sorteo-list/*" element={<SorteoList />} />
          <Route path="/idle/:id" element={<IdlePage />} />
          <Route path="/sorteo/:id/participantes/*" element={<SorteoParticipants />} />
          <Route path="/sorteo/:id/machines/*" element={<SorteoMachines />} />
          <Route path="/idle-has-winner/:id/*" element={<IdleHasWinner />} />
          <Route path="/sorteo-animation/:id" element={<PlaySorteoBySala />} />
          <Route path="/sorteo-animation-3d/:id" element={<PlaySorteo />} />
          <Route path="/sorteo-animation-anfora/:id" element={<PlaySorteoAnfora />} />
          <Route path="/sorteo-animation-hotseat/:id" element={<PlayHotSeatBySala />} />
          <Route path="/sorteo-animation-hotseat-casino/:id" element={<PlayHotSeatCasino />} />
          <Route path="/sorteo-animation-hotseat-3d/:id" element={<PlayHotSeat />} />
          <Route path="/winner-view/:id" element={<CongratWinner />} />
          <Route path="/winner-view-hotseat/:id" element={<CongratHotSeatWinner />} />
          <Route path="/anfora/*" element={<AnforaView />} />
          <Route path="/torneo-view-rank/:id" element={<TorneoViewRank />} />
          <Route path="/torneo-view-final" element={<TorneoViewFinal />} />
          <Route path="/torneo-winner/:id" element={<TorneoWinner />} />
          <Route path="/progressive-view/:id" element={<ProgressiveView />} />
          <Route path="/progressive-winner-view/:id" element={<ProgressiveWinnerView />} />
        </Routes>
      </RequireAuth>
    </SiteProvider>
  )
}

function App() {

  // Se mantiene el salaId en estado para que el tema reaccione al cambiar de sala
  // sin necesidad de refrescar la página.
  const [salaId, setSalaId] = useState(() => localStorage.getItem('salaId'))

  useEffect(() => {
    const handleSalaChange = () => setSalaId(localStorage.getItem('salaId'))
    // 'salaChanged' se dispara al seleccionar sala; 'storage' cubre otras pestañas.
    window.addEventListener('salaChanged', handleSalaChange)
    window.addEventListener('storage', handleSalaChange)
    return () => {
      window.removeEventListener('salaChanged', handleSalaChange)
      window.removeEventListener('storage', handleSalaChange)
    }
  }, [])

  const isDreams = window.location.host === 'dreams.sorteos.wargos.cloud'

  let customTheme = generalColor
  if (isDreams) {
    customTheme = dreamsColor
  } else if (salaId === '332') {
    // Sala 332 usa el tema verde oscuro (fondo, botones y acentos)
    customTheme = greenColor
  }

  return (
    <ThemeProvider theme={customTheme}>
      <WauthProvider
        domain={import.meta.env.REACT_APP_AUTH_SERVER}
        clientId={import.meta.env.REACT_APP_AUTH_CLIENT_ID}
        redirectUri={window.location.origin + '/callback'}
      >
        <GlobalStyles />
        <PageWrapperStyled>
          <PwaUpdatePrompt />
          <PerformanceMonitor />
          <Routes>
            {import.meta.env.MODE === 'development' && <Route path="/preview/progresivo" element={
              <React.Suspense fallback={<div style={{ color: '#ffdf9b', padding: 30 }}>Cargando previews del progresivo…</div>}>
                <ProgressivePreview />
              </React.Suspense>
            } />}
            {import.meta.env.MODE === 'development' && <Route path="/preview/hotseat-casino" element={
              <React.Suspense fallback={<div style={{ color: '#ffdf9b', padding: 30 }}>Cargando la sala…</div>}>
                <HotSeatCasinoPreview />
              </React.Suspense>
            } />}
            {import.meta.env.MODE === 'development' && <Route path="/preview/sorteo-virtual" element={
              <React.Suspense fallback={<div style={{ color: '#ffdf9b', padding: 30 }}>Cargando sorteo virtual…</div>}>
                <VirtualRafflePreview />
              </React.Suspense>
            } />}
            <Route path="/home/*" element={
              <ThemeProvider theme={customTheme}>
                <HomePage salaId={salaId} />
              </ThemeProvider>
            } />
            <Route path="/torneo-view-rank-p/:hash" element={<TorneoViewRankPublic />} />

            <Route path="/login" element={<LoginPage />} />
            <Route path="/callback" element={<CallbackPage />} />
            <Route path="/logout" element={<LogoutPage />} />

            <Route path="/" element={<Navigate to="/home" replace />} />

            <Route path="*" element={<SecureApp />} />
          </Routes>
        </PageWrapperStyled>
      </WauthProvider>
    </ThemeProvider>
  );
}

export default App;
